"use client";
import React, {useEffect, useRef, useState} from "react";
import useAutoplay from "@/components/hooks/useAutoplay";
import Slider, {Slide} from "@/components/ui/swiper";
import {SlideOne as SlideItem} from "@/components/slider/slide/slide-one";
import {NavigationSlider as NavigationItem} from "@/components/slider/slide/navigation-slider";
import styled from "styled-components";
import ThemeRow from "@/components/theme/row";
import {Col} from "react-bootstrap";
import {LatestNews} from "@/components/slider/slide/latest-news";
import {gsap} from "gsap";
import {usePathname} from "next/navigation";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/effect-fade";
import {Swiper, SwiperSlide} from "swiper/react";
import {Autoplay, Navigation, Pagination, EffectFade} from "swiper";


const SliderMain = ({containerGap, data, settings, className, latest}) => {
	// Refs for main home-slider and navigation home-slider
	const mainSliderRef = useRef(null);
	const navigationSliderRef = useRef(null);
	const latestSliderRef = useRef(null);
	
	var pagination_title = [""]; // Array for pagination titles
	const [activeSlideIndex, setActiveSlideIndex] = useState(0); // State for active slide index
	useAutoplay(mainSliderRef); // Hook for autoplay
	
	// Function to handle progress of slides
	const handleProgress = (swiper, event) => {
		const interleaveOffset = 0.5;
		const {slides} = swiper;
		
		slides.forEach((slide) => {
			const slideProgress = slide.progress;
			const innerOffset = swiper.width * interleaveOffset;
			const innerTranslate = slideProgress * innerOffset;
			const globalImage = slide.querySelector(".global-image");
			
			if (globalImage) {
				globalImage.style.transform = `translate3d(${innerTranslate}px, 0, 0)`;
			}
		});
	};
	
	// Function to set transition of slides
	const handleSetTransition = (swiper, speed) => {
		swiper.slides.forEach((slide) => {
			slide.style.transition = `${speed}ms`;
			const image = slide.querySelector(".global-image");
			if (image) {
				image.style.transition = `${speed}ms`;
			}
		});
	};
	
	// Function to handle slide change event
	const handleSlideChange = (event) => {
		const newActiveSlideIndex = event.realIndex;
		setActiveSlideIndex(newActiveSlideIndex);
		if (navigationSliderRef?.current) {
			navigationSliderRef?.current.swiper.slideTo(newActiveSlideIndex);
		}
	};
	
	// Function to handle pagination button click
	const handleButtonClickPagination = (index) => {
		if (mainSliderRef?.current) {
			setActiveSlideIndex(index);
			mainSliderRef?.current.swiper.slideToLoop(index);
		}
	};
	
	// Function to initialize swiper
	const handleSwiperInit = (swiper) => {
		return swiper;
	};
	
	// Pagination configuration
	const pagination = {
		clickable: true,
		renderBullet: function (index, className) {
			return (
				'<div className="' +
				className +
				'"><span>' +
				pagination_title[index] +
				"</span></div>"
			);
		},
	};
	
	const pathName = usePathname();
	
	useEffect(() => {
		gsap.to(".custom-latest-news-wrapper", {
			opacity: 1,
			delay: 1,
			duration: 0.5,
		});
	}, [pathName]);
	
	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-GB", {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
	};
	
	useEffect(() => {
		// Set initial state
		gsap.set(".home-banner", {opacity: 0});
		
		gsap.to(".home-banner", {
			opacity: 1,
			ease: "easeInOut",
			duration: 1,
			delay: 1,
		});
	}, [pathName]);
	
	const [offset, setOffset] = useState(null);
	
	useEffect(() => {
		const theOffset = () => {
			setOffset(document.querySelector(".footer .container").offsetLeft);
		};
		window.addEventListener("resize", theOffset);
		theOffset();
	}, []);
	
	
	// demo length
	return (
		<StyledInteractiveBanner
			offset={offset}
			containerGap={containerGap}
			className={"home-banner"}
		>
			{data?.list && data?.list?.length > 0 && (
				<Slider
					ref={mainSliderRef}
					settings={settings}
					className={className}
					onSwiperInit={handleSwiperInit}
					spaceBetween={0}
					loop={true}
					speed={1000}
					onSlideChange={handleSlideChange}
					onProgress={handleProgress}
					onSetTransition={handleSetTransition}
					grabCursor={false}
					watchSlidesProgress={true}
					mousewheelControl={true}
					keyboardControl={true}
					pagination={pagination}
					navigationLeft={".swiper-button-next"}
					navigationRight={".swiper-button-prev"}
				>
					{data?.list &&
						data?.list?.length > 0 &&
						data?.list?.map((e, index) => {
							if (e?.video) {
								return (
									<Slide key={index}>
										<SlideItem
											topTitle={e?.subtitle}
											title={e?.title}
											video
											bannerLarge={
												e?.video
													? e?.video
													: "/videos/static/sample_video_2.mp4"
											}
											bannerSmall={
												e?.m_video
													? e?.m_video
													: e?.video
														? e?.video
														: "/videos/static/sample_video_2.mp4"
											}
											buttonLabel={e?.button_label}
											buttonLink={e?.button_url}
											buttonTarget={"self"}
											description={e?.desc ? e?.desc : ""}
										/>
									</Slide>
								);
							}
							else {
								return (
									<Slide key={index}>
										<SlideItem
											topTitle={e?.subtitle}
											title={e?.title}
											bannerLarge={
												e?.image ? e?.image : "/images/static/blur_.jpg"
											}
											bannerSmall={
												e?.m_image
													? e?.m_image
													: e?.image
														? e?.image
														: "/images/static/blur_.jpg"
											}
											buttonLabel={e?.button_label}
											buttonLink={e?.button_url}
											buttonTarget={"self"}
											description={e?.desc ? e?.desc : ""}
										/>
									</Slide>
								);
							}
						})}
					
					<div className="swiper-button-prev"/>
					<div className="swiper-button-next"/>
				</Slider>
			)}
			
			<div className={"custom-navigation-home-slider-wrapper"}>
				<ThemeRow>
					<Col md={12}>
						{data?.list && data?.list?.length > 0 && (
							<Slider
								ref={navigationSliderRef}
								speed={100}
								slidesPerView={4}
								loop={false}
								autoplay={false}
								spaceBetween={0}
								grabCursor={false}
								watchSlidesProgress={false}
								mousewheelControl={false}
								keyboardControl={false}
								settings={{
									767: {
										slidesPerView: 2,
										spaceBetween: 0,
									},
									768: {
										slidesPerView: 1,
										spaceBetween: 0,
									},
									1024: {
										slidesPerView: 4,
										spaceBetween: 0,
									},
								}}
							>
								{data?.list &&
									data?.list?.length > 0 &&
									data?.list?.map((e, index) => {
										return (
											<Slide key={index}>
												<NavigationItem
													activeIndex={activeSlideIndex}
													index={index}
													onClick={handleButtonClickPagination}
												/>
											</Slide>
										);
									})}
							</Slider>
						)}
						<p className={'mt-2 text-white font-weight-bold d-inline-flex align-items-center'}>
							<svg fill="#FFD700" version="1.1" id="Capa_1" style={{
								marginRight: '10px'
							}} height={30} width={30} xmlns="http://www.w3.org/2000/svg"
							     viewBox="0 0 550.688 550.688" stroke="#FFD700">
								<g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
								<g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
								<g id="SVGRepo_iconCarrier">
									<g>
										<g>
											<path
												d="M38.166,240.824c13.03,9.92,26.833,17.384,41.616,24.305c6.772,2.969,13.574,5.86,20.41,8.679 c14.07,5.891,21.083,11.646,30.787,19.938c3.853,3.296,3.85,2.837-1.129,1.889c-15.986-3.045-29.578,21.637-13.685,34.979 c16.12,13.531,34.728,11.062,50.245-2.068c4.697-3.975,7.638-8.672,9.223-13.739c1.515-4.838,3.877-5.707,6.992-1.708 c16.713,21.463,34.911,38.504,54.14,48.587c4.489,2.356,8.311,8.002,8.311,13.072v57.632c0,1.766,0.178,3.485,0.496,5.156 c0.535,2.815-2.733,5.003-7.8,5.003h-38.097c-12.537,0-22.699,10.162-22.699,22.699v36.433c0,12.537,10.166,22.699,22.699,22.699 h151.335c12.536,0,22.699-10.162,22.699-22.699v-36.433c0-12.537-10.166-22.699-22.699-22.699h-38.098 c-5.07,0-8.335-2.185-7.8-5.003c0.318-1.671,0.496-3.391,0.496-5.156v-57.632c0-5.07,3.822-10.716,8.311-13.072 c19.229-10.083,37.427-27.124,54.141-48.587c3.115-3.999,5.478-3.13,6.992,1.708c1.585,5.067,4.526,9.761,9.223,13.739 c15.518,13.13,34.126,15.6,50.245,2.068c15.895-13.342,2.302-38.023-13.684-34.979c-4.979,0.948-4.982,1.407-1.129-1.889 c9.703-8.292,16.716-14.048,30.786-19.938c6.836-2.818,13.639-5.71,20.41-8.679c14.783-6.921,28.584-14.385,41.616-24.305 c23.137-17.613,34.241-45.101,37.54-73.082c5.823-49.437-29.762-90.481-74.027-104.646c-4.829-1.545-8.323-6.518-7.824-11.563 c0.551-5.569,0.936-10.93,1.15-16.047c0.211-5.064-3.911-9.177-8.981-9.177H90.312c-5.07,0-9.192,4.109-8.981,9.177 c0.214,5.117,0.6,10.478,1.147,16.047c0.499,5.046-2.996,10.021-7.824,11.567C30.388,77.261-5.197,118.305,0.626,167.742 C3.922,195.723,15.03,223.21,38.166,240.824z M410.761,243.21c21.405-43.55,38.026-92.231,48.052-136.044 c1.132-4.942,5.954-7.879,10.605-5.86c45.555,19.768,62.963,83.7,12.724,115.126c-19.357,12.108-45.82,18.868-67.556,30.392 C410.109,249.202,408.527,247.761,410.761,243.21z M278.244,83.442h31.665v213.254h-35.875V118.06h-0.842L225.5,143.794 l-7.164-28.278L278.244,83.442z M81.27,101.306c4.651-2.02,9.474,0.918,10.606,5.86c10.024,43.816,26.646,92.498,48.051,136.048 c2.237,4.55,0.652,5.988-3.825,3.61c-21.735-11.524-48.198-18.283-67.556-30.392C18.304,185.006,35.715,121.074,81.27,101.306z"></path>
										</g>
									</g>
								</g>
							</svg>
							 #1 Since&nbsp;<span style={{
								 textDecoration: "underline",
						}}>2021</span>,&nbsp; <span style={{
							textDecoration: "underline",
						}}>2022</span>,&nbsp; <span style={{
							textDecoration: "underline",
						}}>2023</span>,&nbsp; <span style={{
							textDecoration: "underline",
						}}>2024</span>,&nbsp; <span style={{
							textDecoration: "underline",
						}}>2025</span>
						</p>
					</Col>
				</ThemeRow>
			</div>
			
			{
				data?.latest_blog &&
				<div className="custom-latest-news-wrapper">
					<ThemeRow>
						<Col md={2}>
							<p className={"latest-news"}>Lastest Insights</p>
						</Col>
						<Col md={8}>
							<div className="slider-news">
								<div className={"custom-latest-news"}>
									<LatestNews
										date={formatDate(latest?.list?.[0]?.post_date_gmt)}
										linkSecond={`/ucb-hub/${latest?.list?.[1]?.post_name}`}
										link={`/ucb-hub/${latest?.list?.[0]?.post_name}`}
										titleSecond={latest?.list?.[1]?.post_title}
										title={latest?.list?.[0]?.post_title}
										dateSecond={formatDate(latest?.list?.[1]?.post_date_gmt)}
									/>
								</div>
							</div>
						</Col>
					</ThemeRow>
				</div>
			}
		
		</StyledInteractiveBanner>
	);
};

export const StyledInteractiveBanner = styled.section`
    height: 100vh;
    position: relative;
    opacity: 1 !important;

    /**
				* all style for swiper slider
			**/

    .swiper-button-prev,
    .swiper-button-next {
        width: ${(p) => p.offset}px;
        height: 100%;
        top: 0;
        margin: 0;
        transition: 0.7s all ease;
        display: block !important;
        z-index: 8;

        &::after {
            display: none;
        }
    }

    .swiper-button-prev {
        left: 0;
        cursor: url("/images/static/slider-left.svg") 32 32, auto;
    }

    .swiper-button-next {
        cursor: url("/images/static/slider-right.svg") 32 32, auto;
    }

    .slider-inner_right {
        .setImageOne {
            .image-size {
                height: 130px !important;
                margin-bottom: 20px;
            }

            &:last-child {
                .image-size {
                    margin-bottom: 0;
                }
            }
        }
    }

    .custom-latest-news-wrapper {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 25px;
        z-index: 9999;
        opacity: 0;

        @media (min-width: 1025px) {
            .container {
                padding-left: 115px;
            }
        }

        p {
            font-size: 16px;
            font-weight: bold;
            line-height: 20px;
            color: white;
        }

        @media (max-width: 992px) {
            .col-md-8 {
                max-width: calc(100% - 16.666667%);
                flex: 0 0 calc(100% - 16.666667%);
            }
        }

        @media (max-width: 767px) {
            .col-md-8 {
                max-width: 100%;
                flex: 100%;
            }

            .latest-news {
                margin-bottom: 20px;
            }

            ul {
                li {
                    margin-bottom: 10px;

                    a {
                        display: flex;
                        flex-wrap: wrap;
                    }

                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }
        }
    }

    .tagDataSet {
        margin-top: 10px;

        a {
            color: black;
            margin-right: 10px;
            background: white;
            border-radius: 5px;
            padding: 5px 8px;
        }
    }

    .swiper-button-prev,
    .swiper-button-next,
    .swiper-pagination,
    .mobile {
        display: none;
    }

    .swiper-slide {
        overflow: hidden;
    }

    .swiper-pagination {
        display: none;
    }

    .swiper-button-disabled {
        opacity: 0.5 !important;
    }

    .top-title-wrapper {
        position: relative;
        overflow: hidden;
        min-height: 20px;

        p {
            position: relative;
            margin: 0 0 40px 0 !important;
            transform: translateX(50px);
            transition: 0.6s all ease;
            /* transition-delay: 0.5s; */
            opacity: 0;
        }
    }

    .title-wrapper-middle {
        position: relative;

        display: flex;
        overflow: hidden !important;
        margin-bottom: 30px;
        @media (min-width: 769px) {
            width: 70%;
        }

        h2 {
            position: relative;
            line-height: 78px;
            transition: 0.6s all ease;
            transition-delay: 0s;
            margin: 0 !important;
            opacity: 0;
            transform: translateY(40px);
        }
    }

    .middle-description {
        position: relative;
        overflow: hidden;

        p {
            position: relative;
            margin: 0 !important;
            transform: translateY(100px);
            transition: 0.7s all ease;
            transition-delay: 0.5s;
        }
    }

    .swiper-slide-active,
    .swiper-slide-visible {
        .global-image {
            clippath: black;
        }

        .title-wrapper-middle {
            position: relative;
            overflow: visible;

            h2 {
                position: relative;
                opacity: 1;
                transition-delay: 0.9s;
                transform: none;
            }
        }

        .middle-description {
            p {
                position: relative;
                margin: 0 !important;
                transform: translateY(0);
                transition-delay: 1s;
            }
        }

        .top-title-wrapper {
            position: relative;

            p {
                opacity: 1;
                transform: translateX(0);
            }
        }

        .slide-inner {
            video {
                position: absolute;
                inset: 0;
                object-fit: cover;
                height: 100%;
                width: 100%;
            }

            .dc-btn {
                z-index: 9;
            }

            img {
                height: 100vh !important;
            }

            &__info {
                //transition-delay: 0.5s;
                //opacity: 1;
                //transform: translateX(0px) !important;

                p {
                    font-size: 16px;
                    font-weight: 400;
                    line-height: 20px;
                    color: white;
                    opacity: 1;
                }

                .dc-btn {
                    transition-delay: 1.2s;
                    opacity: 1 !important;
                    transform: none !important;

                    a {
                        &:after {
                            display: none;
                        }
                    }
                }
            }
        }
    }

    .custom-navigation-home-slider-wrapper {
        display: flex;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 40px;
        min-height: 75px;
        text-align: left;
        margin: 0 auto;
            //padding-left: ${(props) =>
                props.containerGap ? props.containerGap + 15 : "90"}px;
            //padding-right: ${(props) =>
                props.containerGap ? props.containerGap + 15 : "90"}px;
        justify-content: space-between;
        gap: 15px;
        flex-direction: row;
        z-index: 99;

        .swiper-wrapper {
            width: 100%;
            height: 3px;
            background-color: #fff;

            .swiper-slide {
                flex-grow: 1;
            }
        }

        @media (min-width: 1025px) {
            .container {
                padding-left: 115px;
            }
        }

        .swiper {
            opacity: 0;

            &.swiper-initialized {
                opacity: 1 !important;
            }
        }

        .container {
            margin-top: 0;
            margin-bottom: 0;
        }

        @media screen and (min-width: 1024px) {
            .slide-inner-item {
                min-width: 250px;
            }

            .swiper-backface-hidden .swiper-slide {
                min-width: 250px;
            }
        }

        .slide-inner-item {
            width: 100%;
            position: relative;
            background: transparent !important;
            opacity: 1 !important;
            padding: 20px 0 0;
            cursor: pointer;

            @media screen and (min-width: 1536px) {
                padding-right: 15px;
            }

            span {
                font-size: 20px;
                font-weight: 500;
                color: white;
                line-height: 28px;
                opacity: 1 !important;
                transition: 0.7s all ease;
            }

            &:after {
                content: "";
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                height: 4px;
                background: #e62e43;
                width: 0;
                transition: 0.7s all ease;
                opacity: 0;
                z-index: 999;
            }

            &:hover {
                &:after {
                    width: 100%;
                    opacity: 1;
                }
            }

            &:before {
                content: "";
                background: #ffffff !important;
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
                height: 4px;
                top: 0;
                z-index: 99;
            }

            &.active {
                &:after {
                    width: 100%;
                    opacity: 1;
                }
            }
        }

        @media (max-width: 767px) {
            bottom: 100px;
        }

        @media (max-width: 445px) {
            bottom: 80px;
        }
    }

    .slide-inner {
        position: relative;
        height: 100vh;

        video {
            position: absolute;
            inset: 0;
            object-fit: cover;
            height: 100%;
            width: 100%;
        }

        .container {
            position: absolute;
            left: 0;
            right: 0;
            /* top: 50%; */
            /* transform: translateY(-50%); */
            z-index: 2;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
        }

        .global-image {
            will-change: clipPath;
            overflow: hidden;
            -webkit-clippath: polygon(0 0, 100% 0, 100% 100%, 0 100%);
            clippath: polygon(0 0, 100% 0, 100% 100%, 0 100%);

            &:after {
                content: "";
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
                top: 0;
                z-index: 1;
                background: linear-gradient(
                        to right,
                        rgba(0, 0, 0, 0.4),
                        rgba(0, 0, 0, 0)
                );
            }
        }

        &__info {
            position: relative;
            //overflow: hidden;
            //transform: translateX(50%);
            transition: 0.7s all ease;
            //transition-delay: 1.2s;
            //opacity: 0;
            padding-bottom: 5px;

            .container {
                overflow: visible;
            }

            p {
                font-size: 16px;
                font-weight: 400;
                line-height: 20px;
                color: white;
                transition: 0.7s all ease;
                transition-delay: 1.2s;
                //opacity: 0;
                @media (min-width: 1535px) {
                    width: 30% !important;
                }
                @media (min-width: 1024px) {
                    width: 60%;
                }
            }

            @media screen and (min-width: 1024px) {
                width: 100%;
            }

            .dc-btn {
                position: relative;
                transition-delay: 0s;
                //transform: translateX(50%);
                transition: 0.6s all ease;
                opacity: 0;
                transform: translateY(40px);
            }

            .toptitle {
                color: #ffffff;
                font-size: 24px;
                font-weight: 400;
                line-height: 32px;
                letter-spacing: 10px;
                margin-bottom: 40px;

                @media (min-width: 550px) {
                    white-space: nowrap;
                }
            }

            .title {
                transition: 0.7s all ease;
                transition-delay: 1.5s;
                transform: none !important;

                &:after {
                    display: none;
                }
            }

            h1,
            h2 {
                color: #ffffff;
                font-size: 68px;
                line-height: 82px;
                font-weight: 600;
                letter-spacing: 0.5px;
                margin-bottom: 20px;
                text-transform: none;

                &:after {
                    display: none;
                }
            }

            .side-wrapper {
                padding-left: 100px;
                position: relative;

                &:before {
                    position: absolute;
                    left: -${(p) => p.offset + 25}px;
                    height: 1px;
                    background: white;
                    content: "";
                    top: 33px;
                    width: ${(p) => p.offset + 70}px;
                    transition: 0.7s all ease;
                    transition-delay: 0.7s;
                }
            }
        }
    }

    .navigation {
        position: absolute;
        right: ${(props) => (props.containerGap ? props.containerGap : "90")}px;
        top: 50%;
        transform: translateY(-50%);
        display: inline-block;
        z-index: 1;

        ul {
            display: flex;
            flex-direction: column-reverse;

            li {
                cursor: pointer;

                &:first-child {
                    margin-right: 0;
                }

                &:last-child {
                    margin-bottom: 20px;
                }

                svg {
                    path,
                    #Rectangle_5661 {
                        //transition: strokeDasharray 0.3s ease-in-out;
                        transition: 0.7s all ease;
                    }
                }

                &:hover {
                    svg {
                        #Rectangle_5661 {
                            opacity: 1;
                            stroke-dasharray: 140;
                        }

                        path {
                            stroke: #e62e43;
                        }
                    }
                }
            }
        }
    }

    .nav-left {
        position: absolute;
        height: 100%;
        width: 50%;
        left: 0;
        z-index: 9;
        cursor: url("/images/static/slider-left.svg") 32 32, auto;
    }

    .nav-right {
        position: absolute;
        height: 100%;
        width: 50%;
        right: 0;

        z-index: 9;
        cursor: url("/images/static/slider-right.svg") 32 32, auto;
    }

    .swiper-wrapper {
        /* height: 3px; */
    }

    @media screen and (min-width: 1536px) {
        .slide-inner {
            &__info {
                position: relative;
                //overflow: hidden;

                h2 {
                    font-size: 80px;
                    line-height: 100px;
                }

                .dc-btn {
                    a {
                        font-size: 22px;
                        line-height: 33px;
                    }
                }
            }
        }
    }
    @media screen and (max-width: 992px) {
        .custom-navigation-slider-wrapper {
            bottom: 250px;
        }
    }
    @media (max-width: 992px) and (min-width: 768px) {
        height: 100vh;
        .custom-navigation-slider-wrapper {
            bottom: 60px;

            .swiper-initialized {
                padding-right: 30%;
            }
        }

        .slide-inner {
            height: 100svh;

            /* .container {
              top: 45%;
            } */
        }

        .navigation {
            position: absolute;
            right: unset;
            top: unset;
            transform: unset;
            display: inline-block;
            z-index: 1;
            left: ${(props) => (props.containerGap ? props.containerGap : "90")}px;
            bottom: 180px;

            ul {
                flex-direction: row;

                li {
                    &:last-child {
                        margin-bottom: 0;
                        margin-left: 20px;
                    }
                }
            }
        }
    }
    @media screen and (max-width: 767px) {
        height: 100vh;

        .mobile {
            display: block;
        }

        .side-wrapper {
            padding-left: 0 !important;
        }

        .title {
            padding: 0;

            &:before {
                display: none;
            }
        }

        .slide-inner {
            height: 100vh;

            .container {
                top: unset;
                transform: unset;
                /* bottom: 40%; */
            }

            &__info {
                position: relative;
                overflow: hidden;

                .toptitle {
                    margin-bottom: 20px;
                }

                h2,
                h1 {
                    font-size: 52px;
                    line-height: 60px;
                    font-weight: 600;
                }

                .dc-btn {
                    margin-top: 30px;
                }
            }
        }

        .custom-navigation-slider-wrapper {
            padding-left: 15px !important;
            padding-right: 15px !important;
            bottom: 30px;

            .swiper-initialized {
                padding-right: 30%;
            }
        }

        .scrollTo {
            left: 15px;
            right: unset;
            display: inline-block;
            bottom: 20px;
            transform: translateX(0);
        }

        .navigation {
            display: none;
        }
    }
    @media (max-width: 1300px) and (min-width: 1024px) {
        .slide-inner__info h2, .slide-inner__info h1 {
            font-size: 50px;
            line-height: 65px;
        }
    }
    @media (max-width: 991px) {
        .slide-inner__info .side-wrapper:before {
            display: none;
        }

        .slide-inner .container {
            height: 70%;
        }
    }

    @media (max-width: 767px) {
        .slide-inner .container {
            height: 90%;
        }

    }
`;

export default React.memo(SliderMain);
